/**
 * ContactListShared.tsx
 *
 * Shared components and helpers used by the Contact Lists page and detail page.
 * Contains: FilterPanel, PreviewSection, filter group components, helper functions.
 */
import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronDown, ChevronRight, Search,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { Spinner } from '@/components/ui/Spinner';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { cn, formatPhone } from '@/lib/utils';
import { useGlobalFacilityStore } from '@/store/globalFacilityStore';
import * as marketingSvc from '@/services/marketing.service';
import type { DateRange } from '@/types/common.types';
import type { FilterNegations } from '@/types/filters.types';
import { isDateRangePresetKey, resolvePreset } from '@/lib/dateRangePresets';

// ─── Constants ────────────────────────────────────────────────────────────────

export const SOURCE_TYPE_OPTIONS = [
  { value: 'patient', label: 'Patients' },
  { value: 'app_users', label: 'App Users (portal / push)' },
  { value: 'membership', label: 'Memberships' },
  { value: 'member', label: 'Members (Unique)' },
  { value: 'appointment', label: 'Appointments' },
  { value: 'payment', label: 'Payments' },
  { value: 'plan', label: 'Plans' },
  { value: 'package', label: 'Packages' },
  { value: 'integration_order', label: 'Marketplace Orders' },
];

export const LIST_TYPE_OPTIONS = [
  { value: 'static' as const, label: 'Static (manual members)' },
  { value: 'dynamic' as const, label: 'Dynamic (from segment)' },
];

export const AUTOMATION_STATUS_STYLES: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  stopped: 'bg-red-100 text-red-700',
};

// ─── Filter Panel ─────────────────────────────────────────────────────────────

export interface FilterPanelProps {
  sourceType: string;
  onSourceTypeChange: (st: string) => void;
  filterValues: Record<string, string[] | { from: string; to: string }>;
  onFilterChange: (key: string, val: string[] | { from: string; to: string } | null) => void;
}

export function FilterPanel({ sourceType, onSourceTypeChange, filterValues, onFilterChange }: FilterPanelProps) {
  const { data: rawFilterGroups = [], isLoading, isError, error } = useQuery({
    queryKey: ['marketing', 'segment-filters', sourceType],
    queryFn: () => marketingSvc.getSegmentFilters(sourceType),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const facilityLocked = useGlobalFacilityStore((s) => s.selectedFacilityIds.length > 0);
  const filterGroups = useMemo(
    () => (facilityLocked ? rawFilterGroups.filter((g) => g.key !== 'facilities') : rawFilterGroups),
    [facilityLocked, rawFilterGroups],
  );

  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const active = new Set<string>();
    for (const g of filterGroups) {
      const val = filterValues[g.key];
      if (val) {
        if (Array.isArray(val) && val.length > 0) active.add(g.key);
        else if (!Array.isArray(val) && (val.from || val.to)) active.add(g.key);
      }
    }
    if (active.size > 0) {
      setExpandedSections((prev) => new Set([...prev, ...active]));
    }
  }, [filterGroups, filterValues]);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const optionValueSets = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    for (const g of filterGroups) {
      if (g.options?.length) map[g.key] = new Set(g.options.map((o) => o.value));
    }
    return map;
  }, [filterGroups]);

  const getCheckedCount = (key: string): number => {
    const val = filterValues[key];
    if (!val) return 0;
    if (Array.isArray(val)) {
      const allowed = optionValueSets[key];
      return allowed ? val.filter((v) => allowed.has(v)).length : val.length;
    }
    if (val.from || val.to) return 1;
    return 0;
  };

  const effectiveFilterCount = useMemo(() => {
    let count = 0;
    for (const g of filterGroups) {
      count += getCheckedCount(g.key);
    }
    return count;
  }, [filterGroups, filterValues, optionValueSets]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="w-80 flex-shrink-0 bg-slate-50 border border-slate-200 rounded-lg p-3 flex flex-col h-full overflow-hidden">
      <div className="flex items-center gap-3 mb-3">
        <h3 className="text-sm font-semibold text-slate-900">Filters</h3>
        {effectiveFilterCount > 0 && (
          <Badge variant="info">{effectiveFilterCount}</Badge>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
        <label className="block text-xs font-semibold text-slate-700 mb-1.5">Data Source</label>
        <Select
          value={sourceType}
          onChange={(v) => onSourceTypeChange(v ?? 'patient')}
          options={SOURCE_TYPE_OPTIONS}
        />
        <p className="text-[11px] text-slate-500 mt-1">Select the type of data to filter</p>
      </div>

      <div className="flex-1 overflow-y-auto space-y-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="sm" label="Loading filters..." />
          </div>
        ) : isError ? (
          <div className="text-xs text-red-600 text-center py-4 px-2">
            <p className="font-medium">Failed to load filters</p>
            <p className="mt-1 text-red-500">{(error as any)?.response?.data?.error || (error as Error)?.message || 'Unknown error'}</p>
          </div>
        ) : filterGroups.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-4">No filters available for this source.</p>
        ) : (
          filterGroups.map((group) => {
            const isExpanded = expandedSections.has(group.key);
            const checkedCount = getCheckedCount(group.key);

            return (
              <div key={group.key} className="border border-slate-200 rounded-md bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection(group.key)}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    {group.label}
                    {checkedCount > 0 && (
                      <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-blue-600 text-white text-[11px] font-semibold">
                        {checkedCount}
                      </span>
                    )}
                  </span>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-3 pb-3 border-t border-slate-100">
                    {group.type === 'checkbox' && group.options && (
                      <CheckboxFilterGroup
                        filterKey={group.key}
                        options={group.options}
                        selected={Array.isArray(filterValues[group.key]) ? (filterValues[group.key] as string[]) : []}
                        onChange={(vals) => onFilterChange(group.key, vals.length > 0 ? vals : null)}
                      />
                    )}
                    {group.type === 'daterange' && (
                      <DateRangeFilterGroup
                        value={
                          !Array.isArray(filterValues[group.key]) && filterValues[group.key]
                            ? (filterValues[group.key] as { from: string; to: string })
                            : { from: '', to: '' }
                        }
                        onChange={(val) => onFilterChange(group.key, val.from || val.to ? val : null)}
                      />
                    )}
                    {group.type === 'range' && (
                      <RangeFilterGroup
                        min={group.min ?? 0}
                        max={group.max ?? 120}
                        value={Array.isArray(filterValues[group.key]) ? (filterValues[group.key] as string[]) : []}
                        onChange={(vals) => onFilterChange(group.key, vals)}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Checkbox Filter Group ──────────────────────────────────────────────────

function CheckboxFilterGroup({
  filterKey,
  options,
  selected,
  onChange,
}: {
  filterKey: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (vals: string[]) => void;
}) {
  const [filterText, setFilterText] = useState('');
  const filtered = filterText
    ? options.filter((o) => o.label.toLowerCase().includes(filterText.toLowerCase()))
    : options;

  const toggleValue = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="pt-2 space-y-2">
      {options.length > 8 && (
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
          <input
            type="text"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Filter..."
            className="w-full pl-7 pr-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      )}
      <div className="max-h-48 overflow-y-auto space-y-1">
        {filtered.map((opt) => (
          <Checkbox
            key={`${filterKey}-${opt.value}`}
            label={opt.label}
            checked={selected.includes(opt.value)}
            onChange={() => toggleValue(opt.value)}
            wrapperClassName="py-0.5"
          />
        ))}
        {filtered.length === 0 && (
          <p className="text-xs text-slate-400 py-2 text-center">No options match</p>
        )}
      </div>
      {selected.length > 0 && (
        <button
          type="button"
          onClick={() => onChange([])}
          className="text-xs text-primary hover:opacity-90"
        >
          Clear all ({selected.length})
        </button>
      )}
    </div>
  );
}

// ─── Date Range Filter Group ────────────────────────────────────────────────

function DateRangeFilterGroup({
  value,
  onChange,
}: {
  value: { from: string; to: string };
  onChange: (val: { from: string; to: string }) => void;
}) {
  const dateRange: DateRange = {
    startDate: value.from ? new Date(value.from + 'T00:00:00') : null,
    endDate: value.to ? new Date(value.to + 'T00:00:00') : null,
  };

  const handleChange = (range: DateRange) => {
    const from = range.startDate ? range.startDate.toISOString().split('T')[0] : '';
    const to = range.endDate ? range.endDate.toISOString().split('T')[0] : '';
    onChange({ from, to });
  };

  const presets = [
    { label: 'Last 7 days', value: 'last7' },
    { label: 'Last 30 days', value: 'last30' },
    { label: 'Last 90 days', value: 'last90' },
    { label: 'This year', value: 'thisYear' },
  ];

  const applyPreset = (preset: string) => {
    const now = new Date();
    const to = now.toISOString().split('T')[0];
    let from = '';
    if (preset === 'last7') {
      from = new Date(now.getTime() - 7 * 86400000).toISOString().split('T')[0];
    } else if (preset === 'last30') {
      from = new Date(now.getTime() - 30 * 86400000).toISOString().split('T')[0];
    } else if (preset === 'last90') {
      from = new Date(now.getTime() - 90 * 86400000).toISOString().split('T')[0];
    } else if (preset === 'thisYear') {
      from = `${now.getFullYear()}-01-01`;
    }
    onChange({ from, to });
  };

  return (
    <div className="pt-2 space-y-3">
      <div className="flex flex-wrap gap-1">
        {presets.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => applyPreset(p.value)}
            className="text-[11px] px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
          >
            {p.label}
          </button>
        ))}
      </div>
      <DateRangePicker
        value={dateRange}
        onChange={handleChange}
        startLabel="From"
        endLabel="To"
      />
      {(value.from || value.to) && (
        <button
          type="button"
          onClick={() => onChange({ from: '', to: '' })}
          className="text-xs text-red-600 hover:text-red-800"
        >
          Clear dates
        </button>
      )}
    </div>
  );
}

// ─── Range Filter Group (min/max, e.g. Age) ───────────────────────────────────

function RangeFilterGroup({
  min,
  max,
  value,
  onChange,
}: {
  min: number;
  max: number;
  value: string[];
  onChange: (vals: string[]) => void;
}) {
  const curMin = value[0] ?? String(min);
  const curMax = value[1] ?? String(max);
  const isActive = Number(curMin) > min || Number(curMax) < max;

  const emit = (lo: string, hi: string) => {
    if (Number(lo) <= min && Number(hi) >= max) {
      onChange([]);
    } else {
      onChange([lo, hi]);
    }
  };

  return (
    <div className="pt-2 space-y-2">
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={min}
          max={max}
          value={curMin}
          onChange={(e) => emit(e.target.value || String(min), curMax)}
          className="w-20"
        />
        <span className="text-slate-400 text-sm">to</span>
        <Input
          type="number"
          min={min}
          max={max}
          value={curMax}
          onChange={(e) => emit(curMin, e.target.value || String(max))}
          className="w-20"
        />
      </div>
      {isActive && (
        <button
          type="button"
          onClick={() => onChange([])}
          className="text-xs text-red-600 hover:text-red-800"
        >
          Clear
        </button>
      )}
    </div>
  );
}

// ─── Preview Section ──────────────────────────────────────────────────────────

export interface PreviewSectionProps {
  sourceType: string;
  filterConditions: unknown;
  segmentId?: number;
  knownCount?: number;
  dirty?: boolean;
  onMetricsChange?: (metrics: { total: number; uniqueEmailCount: number; uniquePhoneCount: number }) => void;
}

// `dirty` is accepted and not read. Its default lived only here, so dropping
// it from the destructure changes nothing; the prop stays on
// PreviewSectionProps because callers pass it.
export function PreviewSection({ sourceType, filterConditions, segmentId, onMetricsChange }: PreviewSectionProps) {
  const [previewPage, setPreviewPage] = useState(1);
  const [previewSearch, setPreviewSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const pageSize = 25;

  const previewQuery = useQuery({
    queryKey: ['marketing', 'segment-preview', sourceType, JSON.stringify(filterConditions), previewPage, previewSearch],
    queryFn: () =>
      segmentId
        ? marketingSvc.previewSegment(segmentId, { page: previewPage, perPage: pageSize, search: previewSearch })
        : marketingSvc.previewSegmentFilters({
            sourceType,
            filterConditions,
            page: previewPage,
            perPage: pageSize,
            search: previewSearch,
          }),
  });

  const totalCount = previewQuery.data?.total ?? 0;
  const recipients = previewQuery.data?.recipients ?? [];
  const totalPages = Math.ceil(totalCount / pageSize);

  useEffect(() => {
    if (previewQuery.data && onMetricsChange && !previewSearch) {
      onMetricsChange({
        total: previewQuery.data.total ?? 0,
        uniqueEmailCount: previewQuery.data.uniqueEmailCount ?? 0,
        uniquePhoneCount: previewQuery.data.uniquePhoneCount ?? 0,
      });
    }
  }, [previewQuery.data, onMetricsChange, previewSearch]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPreviewPage(1);
  }, [sourceType, filterConditions]);

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setPreviewSearch(searchInput);
      setPreviewPage(1);
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setPreviewSearch('');
    setPreviewPage(1);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative h-full">
      {previewQuery.isFetching && (
        <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center rounded-lg">
          <Spinner size="lg" label="Loading..." />
        </div>
      )}

      {/* Search bar */}
      <div className="flex gap-2 mb-3 flex-shrink-0">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search contacts..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-blue-400"
          />
        </div>
        {searchInput && (
          <Button variant="danger" size="sm" onClick={clearSearch}>
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto border border-slate-200 rounded-md bg-white min-h-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 sticky top-0">
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Email</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Phone</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">First Name</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Last Name</th>
            </tr>
          </thead>
          <tbody>
            {recipients.length === 0 && !previewQuery.isFetching ? (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-slate-500 text-sm">
                  {previewSearch ? 'No results match your search' : 'No matching contacts found'}
                </td>
              </tr>
            ) : (
              recipients.map((r, i) => (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-slate-700">{r.email || '--'}</td>
                  <td className="px-3 py-2 text-slate-700">{r.phone ? formatPhone(r.phone) : '--'}</td>
                  <td className="px-3 py-2 text-slate-700">{r.firstName || '--'}</td>
                  <td className="px-3 py-2 text-slate-700">{r.lastName || '--'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-3 flex-shrink-0 flex-wrap">
          <button
            onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
            disabled={previewPage <= 1}
            className="px-3 py-1 text-xs border border-slate-200 rounded bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const start = Math.max(1, Math.min(previewPage - 2, totalPages - 4));
            const page = start + i;
            if (page > totalPages) return null;
            return (
              <button
                key={page}
                onClick={() => setPreviewPage(page)}
                className={cn(
                  'px-3 py-1 text-xs border rounded',
                  page === previewPage
                    ? 'bg-primary text-white border-primary'
                    : 'border-slate-200 bg-white hover:bg-slate-50',
                )}
              >
                {page}
              </button>
            );
          })}
          <button
            onClick={() => setPreviewPage((p) => Math.min(totalPages, p + 1))}
            disabled={previewPage >= totalPages}
            className="px-3 py-1 text-xs border border-slate-200 rounded bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
          <span className="text-xs text-slate-500 ml-2">
            Showing {(previewPage - 1) * pageSize + 1}-{Math.min(previewPage * pageSize, totalCount)} of {totalCount.toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Helper: Build filter conditions payload ────────────────────────────────

/**
 * Build the `filter_conditions` JSON saved on a dynamic list's segment.
 *
 * Negations are written twice, on purpose, because two stacks read this row:
 *   - top-level `filterPlus: string[]` — the v5 contract (same shape the
 *     marketing modal saves and ContactListResolverService merges in);
 *   - `filtersArray['filter-plus']` as `{ key: false }` — the legacy pane map
 *     where FALSE means negative, which legacy buildFilterQuery() and the
 *     legacy list editor still read.
 * FilterNormalizer::negatedKeys() accepts both and setting a flag twice is
 * idempotent, so the duplication cannot disagree with itself.
 */
export function buildFilterConditions(
  sourceType: string,
  filterValues: Record<string, string[] | { from: string; to: string }>,
  negations: FilterNegations = {},
): Record<string, unknown> {
  const filtersArray: Record<string, unknown> = {};
  const negatedKeys: string[] = [];
  for (const [key, val] of Object.entries(filterValues)) {
    let emitted = false;
    if (Array.isArray(val) && val.length > 0) {
      filtersArray[key] = val;
      emitted = true;
    } else if (!Array.isArray(val) && (val.from || val.to)) {
      filtersArray[key] = `${val.from},${val.to}`;
      emitted = true;
    }
    if (emitted && negations[key] === true) negatedKeys.push(key);
  }
  if (negatedKeys.length > 0) {
    filtersArray['filter-plus'] = Object.fromEntries(negatedKeys.map((k) => [k, false]));
  }
  return {
    mode: 'pane_filters',
    source_type: sourceType,
    filtersArray,
    ...(negatedKeys.length > 0 ? { filterPlus: negatedKeys } : {}),
  };
}

/**
 * Read every spelling of "these filters are negated" a saved list can carry
 * (mirrors FilterNormalizer::negatedKeys on the API):
 *   - `['facility']`                 v5 list of keys
 *   - `{ facility: false }`          legacy pane map, FALSE == negative
 *   - `'facility:1;tags:0'`          legacy raw string, 1 == negative
 */
export function parseNegatedKeys(raw: unknown): string[] {
  if (raw == null || raw === '') return [];
  if (typeof raw === 'string') {
    return raw
      .split(';')
      .map((group) => group.split(':'))
      .filter(([key, flag]) => key?.trim() && (flag ?? '').trim() === '1')
      .map(([key]) => key.trim());
  }
  if (Array.isArray(raw)) {
    return raw.filter((k): k is string => typeof k === 'string' && k.trim() !== '').map((k) => k.trim());
  }
  if (typeof raw === 'object') {
    return Object.entries(raw as Record<string, unknown>)
      .filter(([, flag]) => flag === false || flag === 0 || flag === '0' || flag === 'false')
      .map(([key]) => key);
  }
  return [];
}

function normalizeDateToIso(d: string): string {
  const s = (d ?? '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) {
    const [, mm, dd, yyyy] = us;
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? '' : dt.toISOString().slice(0, 10);
}

function isoToUsDate(iso: string): string {
  const m = (iso ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const [, y, mm, dd] = m;
  return `${mm}/${dd}/${y}`;
}

/**
 * After the full filter unification, React option values for index-based
 * status filters are the SAME numeric index strings legacy stores
 * (e.g. "0" = Manually Entered for payment-status). This function
 * handles backward compat: if old saved data somehow contains slug
 * strings instead of indices, convert them BACK to indices so they
 * match the React option values.
 *
 * BACKWARD COMPAT: NEVER remove entries from these maps. Saved dynamic
 * lists (email_segments.filter_conditions) may contain any of these.
 * Only ADD new entries — never delete or reorder existing ones.
 */
const SLUG_TO_INDEX_MAPS: Record<string, Record<string, string>> = {
  'payment-status': { manually_entered: '0', pending: '1', settled: '2', voided: '3', declined: '4', refund: '5' },
  'payment_status': { manually_entered: '0', pending: '1', settled: '2', voided: '3', declined: '4', refund: '5' },
  'membership-status': { active: '0', added: '0', declined: '1', past_due: '1', cancelled: '2', frozen: '3' },
  'membership_status': { active: '0', added: '0', declined: '1', past_due: '1', cancelled: '2', frozen: '3' },
  'package-status': { active: '0', added: '0', declined: '1', inactive: '2' },
  'package_status': { active: '0', added: '0', declined: '1', inactive: '2' },
};

function normalizeLegacyValues(rawKey: string, _uiKey: string, values: string[]): string[] {
  const slugMap = SLUG_TO_INDEX_MAPS[rawKey];
  if (!slugMap) return values;

  return values.map((v) => {
    if (/^\d+$/.test(v)) return v;
    const lower = v.toLowerCase();
    if (slugMap[lower] !== undefined) return slugMap[lower];
    return v;
  });
}

/**
 * Map of all known stored key variants (legacy underscore, camelCase, etc.)
 * to the canonical kebab-case key used by FilterConfig arrays.
 * Keys already in canonical form pass through unchanged.
 * BACKWARD COMPAT: Only ADD entries — never remove.
 */
const STORED_KEY_TO_CANONICAL: Record<string, string> = {
  plan_type: 'plan-type',
  planTypes: 'plan-type',
  planType: 'plan-type',
  appt_type: 'appt-type',
  appointmentType: 'appt-type',
  lead_source: 'lead-source',
  referralSource: 'lead-source',
  lead_sources: 'lead-source',
  membership_template: 'membership-type',
  membership_type: 'membership-type',
  membershipType: 'membership-type',
  membership_status: 'membership-status',
  membershipStatus: 'membership-status',
  plan_stages: 'plan-stages',
  planStages: 'plan-stages',
  plan_status: 'plan-status',
  planStatus: 'plan-status',
  package_status: 'package-status',
  packageStatus: 'package-status',
  package_type: 'package-type',
  packageType: 'package-type',
  payment_status: 'payment-status',
  paymentStatus: 'payment-status',
  payment_type: 'payment-type',
  paymentType: 'payment-type',
  payType: 'payment-type',
  appointment_status: 'appointment-status',
  appointmentStatus: 'appointment-status',
  appt_status: 'appointment-status',
  tag_ids: 'tags',
  tagIds: 'tags',
  tag: 'tags',
  userIds: 'users',
  user: 'users',
};

function canonicalizeKey(rawKey: string): string {
  return STORED_KEY_TO_CANONICAL[rawKey] ?? rawKey;
}

/**
 * Parse the semicolon-delimited filter string used by legacy PHP.
 * Format: "key:val1,val2;key2:val3;" — mirrors PHP parseFilters().
 * Some save paths store filtersArray as this raw string instead of a
 * pre-parsed object.
 */
function parseFilterString(str: string): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  const groups = str.split(';').filter(Boolean);
  for (const group of groups) {
    const colonIdx = group.indexOf(':');
    if (colonIdx < 1) continue;
    const key = group.slice(0, colonIdx);
    const valStr = group.slice(colonIdx + 1);
    if (!valStr) continue;
    const vals = valStr.split(',').filter(Boolean);
    if (vals.length) result[key] = vals;
  }
  return result;
}

/**
 * Parse stored filter_conditions JSON (from email_segments table) into
 * the format React's filter state expects.
 *
 * Handles BOTH storage formats legacy PHP produces:
 *   1. filtersArray as object  — from parseFilters() in email-list-details.php / list_send.php
 *   2. filtersArray as string  — from list_send_new.php (raw semicolon-delimited)
 *   3. top-level filterPlus    — string-based format stores negations here, not inside filtersArray
 */
export function parseFilterConditions(
  filterConditions: unknown,
): {
  sourceType?: string;
  filterValues: Record<string, string[] | { from: string; to: string }>;
  negations: FilterNegations;
} | null {
  if (!filterConditions || typeof filterConditions !== 'object') return null;
  const fc = filterConditions as Record<string, unknown>;

  const result: {
    sourceType?: string;
    filterValues: Record<string, string[] | { from: string; to: string }>;
    negations: FilterNegations;
  } = {
    filterValues: {},
    negations: {},
  };
  if (fc.mode === 'pane_filters' && typeof fc.source_type === 'string') {
    result.sourceType = fc.source_type;
  }

  // Resolve filtersArray — could be an object (parsed) or a string (raw)
  let fa: Record<string, unknown>;
  if (fc.filtersArray && typeof fc.filtersArray === 'object') {
    fa = fc.filtersArray as Record<string, unknown>;
  } else if (typeof fc.filtersArray === 'string' && fc.filtersArray !== '') {
    // Try JSON first (some paths double-encode), then semicolon format
    try {
      const parsed = JSON.parse(fc.filtersArray);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        fa = parsed as Record<string, unknown>;
      } else {
        fa = parseFilterString(fc.filtersArray);
      }
    } catch {
      fa = parseFilterString(fc.filtersArray);
    }
  } else {
    return result;
  }

  // Negations: top-level filterPlus / filter-plus (v5 + legacy string format)
  // and the legacy map inside filtersArray. Keys are canonicalized the same
  // way as the values so the toggle lands on the filter it belongs to.
  for (const raw of [fc.filterPlus, fc['filter-plus'], fa['filter-plus']]) {
    for (const rawKey of parseNegatedKeys(raw)) {
      result.negations[canonicalizeKey(rawKey)] = true;
    }
  }

  for (const [rawKey, val] of Object.entries(fa)) {
    if (rawKey === 'filter-plus') continue;
    const key = canonicalizeKey(rawKey);
    const isDate = key.includes('daterange') || key.includes('date');

    if (Array.isArray(val)) {
      if (isDate) {
        result.filterValues[key] = buildDateValue(val.map(String));
      } else {
        const cleaned = (val as unknown[]).map(String).filter((v) => v !== '');
        if (cleaned.length) {
          result.filterValues[key] = normalizeLegacyValues(rawKey, key, cleaned);
        }
      }
    } else if (typeof val === 'string') {
      const parts = val.split(',');
      if (isDate) {
        result.filterValues[key] = buildDateValue(parts);
      } else {
        const cleaned = parts.filter(Boolean);
        if (cleaned.length) {
          result.filterValues[key] = normalizeLegacyValues(rawKey, key, cleaned);
        }
      }
    }
  }
  return result;
}

/**
 * Build a DateRangeValue from the stored array/parts.
 * Legacy stores dates as [from, to, presetKey?]. If a preset key is present
 * and valid, re-resolve the dates from the preset (they stay relative to "now")
 * — this mirrors PHP's behavior on page load.
 */
function buildDateValue(parts: string[]): { from: string; to: string; preset?: string } {
  const rawFrom = parts[0] ?? '';
  const rawTo = parts[1] ?? '';
  const presetKey = (parts[2] ?? '').trim();

  if (presetKey && isDateRangePresetKey(presetKey)) {
    const resolved = resolvePreset(presetKey);
    return { from: resolved.from, to: resolved.to, preset: presetKey };
  }

  return {
    from: normalizeDateToIso(rawFrom),
    to: normalizeDateToIso(rawTo),
  };
}

export function composeFilterConditionsForSave(
  originalFilterConditions: unknown,
  sourceType: string,
  filterValues: Record<string, string[] | { from: string; to: string }>,
): Record<string, unknown> {
  const base =
    originalFilterConditions && typeof originalFilterConditions === 'object'
      ? { ...(originalFilterConditions as Record<string, unknown>) }
      : {};
  const filtersArray: Record<string, unknown> =
    base.filtersArray && typeof base.filtersArray === 'object'
      ? { ...(base.filtersArray as Record<string, unknown>) }
      : {};

  // Clear keys the panel can edit; preserve filter-plus and unmappable legacy keys.
  for (const key of Object.keys(filterValues)) {
    delete filtersArray[key];
  }

  for (const [key, val] of Object.entries(filterValues)) {
    if (Array.isArray(val) && val.length > 0) {
      filtersArray[key] = val;
    } else if (!Array.isArray(val) && (val.from || val.to || (val as any).preset)) {
      const preset = (val as any).preset ?? '';
      const fromStr = key === 'daterange' ? isoToUsDate(val.from) : val.from;
      const toStr = key === 'daterange' ? isoToUsDate(val.to) : val.to;
      filtersArray[key] = preset
        ? [fromStr, toStr, preset]
        : `${fromStr},${toStr}`;
    }
  }

  return {
    ...base,
    mode: base.mode ?? 'pane_filters',
    source_type: sourceType,
    filtersArray,
  };
}
